import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, DoorOpen, Loader2, ChevronRight } from 'lucide-react';
import { residentVisitorsAPI } from '../../lib/api';
import VisitorConversation from '../../components/property/VisitorConversation';

const fmtTime = (iso, lang) => {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleString(lang === 'fr' ? 'fr-CA' : lang === 'es' ? 'es-MX' : 'en-CA',
            { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch (_e) { return ''; }
};

export const VisitorsScreen = ({ tx, lang, token, onBack, surface, onUnread }) => {
    const [list, setList] = useState(null);
    const [openId, setOpenId] = useState(null);
    const [thread, setThread] = useState(null);
    const [input, setInput] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const deadRef = useRef(false);

    const labels = {
        empty: tx.vstThreadEmpty, placeholder: tx.vstPlaceholder, notifyVisitor: tx.vstNotify,
        notifiedSent: tx.vstNotified, closed: tx.vstClosed, privacyNote: tx.vstPrivacy,
        you: tx.vstYou, visitor: tx.vstVisitor, autoReply: tx.vstAutoReply, chose: tx.vstChose,
        atDoorNow: tx.vstAtDoorNow, notAtDoor: tx.vstNotAtDoor,
    };

    const loadList = useCallback(async () => {
        try {
            const { data } = await residentVisitorsAPI.list(token);
            setList(data);
            onUnread?.(data.unread_total || 0);
        } catch (_e) { setList((l) => l || { visitors: [] }); }
    }, [token, onUnread]);

    const loadThread = useCallback(async (sid) => {
        try {
            const { data } = await residentVisitorsAPI.thread(token, sid);
            setThread(data);
            deadRef.current = false;
        } catch (e) {
            // Dead-thread recovery: a conversation that no longer exists is
            // surfaced, never silently polled forever.
            if ([404, 410].includes(e?.response?.status)) {
                deadRef.current = true;
                setThread((t) => ({ ...(t || { messages: [] }), thread_closed: true, can_reply: false }));
            }
        }
    }, [token]);

    useEffect(() => {
        loadList();
        const id = setInterval(loadList, 8000);
        return () => clearInterval(id);
    }, [loadList]);

    useEffect(() => {
        if (!openId) return;
        loadThread(openId);
        const id = setInterval(() => { if (!deadRef.current) loadThread(openId); }, 5000);
        return () => clearInterval(id);
    }, [openId, loadThread]);

    const open = (sid) => { setThread(null); setInput(''); setError(''); setOpenId(sid); };
    const closeThread = () => { setOpenId(null); setThread(null); loadList(); };

    const send = async () => {
        const text = input.trim();
        if (!text || !openId) return;
        setBusy(true); setError(''); setInput('');
        try {
            const { data } = await residentVisitorsAPI.reply(token, openId, text);
            setThread((t) => ({ ...(t || {}), messages: [...((t || {}).messages || []), data.message] }));
        } catch (e) {
            setInput(text);
            setError(e?.response?.status === 410 ? tx.vstClosed : tx.vstErr);
            if (e?.response?.status === 410) { deadRef.current = true; setThread((t) => ({ ...(t || {}), thread_closed: true, can_reply: false })); }
        } finally { setBusy(false); }
    };

    const notify = async () => {
        if (!openId) return;
        setBusy(true); setError('');
        try {
            const { data } = await residentVisitorsAPI.notify(token, openId);
            setThread((t) => ({ ...(t || {}), notified_at: data.notified_at }));
        } catch (e) {
            setError(e?.response?.status === 410 ? tx.vstClosed : tx.vstErr);
        } finally { setBusy(false); }
    };

    const back = (
        <button type="button" onClick={openId ? closeThread : onBack}
            data-testid={openId ? 'vst-back-list' : 'vst-back'}
            className="mb-4 flex items-center gap-1 text-sm text-cyan-300 hover:text-cyan-200">
            <ArrowLeft className="h-4 w-4" />{openId ? tx.vstBack : (tx.changeRoom || 'Back')}
        </button>
    );

    const header = (
        <div className="mb-4">
            <div className="mb-1 flex items-center gap-2.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-cyan-400/30 bg-gradient-to-br from-cyan-500/20 to-cyan-400/5 shadow-[0_0_16px_rgba(34,211,238,0.15)]">
                    <DoorOpen className="h-5 w-5 text-cyan-300" />
                </span>
                <h2 className="text-lg font-semibold text-white">{tx.vstTitle}</h2>
            </div>
            {list?.unit_number && (
                <p className="text-sm text-slate-400" data-testid="vst-unit">{tx.vstUnit} {list.unit_number}</p>
            )}
        </div>
    );

    if (openId) {
        const v = (list?.visitors || []).find((x) => x.session_id === openId) || {};
        return (
            <div data-testid="vst-thread-screen">
                {back}
                {header}
                <div className={`${surface} rounded-2xl border border-cyan-500/10 p-4 shadow-[0_0_24px_rgba(34,211,238,0.05)]`}>
                    <p className="mb-0.5 text-sm font-medium text-white">{tx.vstVisitorAt} {thread?.entrance || v.entrance || ''}</p>
                    <p className="mb-3 text-xs text-slate-400" data-testid="vst-thread-started">
                        {tx.vstStarted} {fmtTime(thread?.started_at || v.started_at, lang)}
                    </p>
                    {error && <p className="mb-2 text-sm text-rose-400" data-testid="vst-error">{error}</p>}
                    {thread === null ? (
                        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-cyan-300" /></div>
                    ) : (
                        <VisitorConversation prefix="vst" labels={labels}
                            messages={thread.messages || []} input={input} onInputChange={setInput}
                            onSend={send} onNotify={thread.can_reply ? notify : null}
                            live={!!thread.live} closed={!!thread.thread_closed}
                            notifiedAt={thread.notified_at} busy={busy} />
                    )}
                </div>
            </div>
        );
    }

    return (
        <div data-testid="vst-screen">
            {back}
            {header}
            {list === null ? (
                <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-cyan-300" /></div>
            ) : (list.visitors || []).length === 0 ? (
                <p className={`${surface} rounded-2xl border border-cyan-500/10 px-4 py-10 text-center text-sm text-slate-400`}
                    data-testid="vst-empty">{tx.vstEmpty}</p>
            ) : (
                <div className="space-y-2">
                    {list.visitors.map((v) => (
                        <button key={v.session_id} type="button" onClick={() => open(v.session_id)}
                            data-testid={`vst-row-${v.session_id}`}
                            className="flex w-full items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3.5 text-left transition-colors hover:border-cyan-400/40 hover:bg-white/[0.06]">
                            <div className="min-w-0">
                                <p className="truncate text-sm font-semibold text-white">
                                    {tx.vstVisitorAt} {v.entrance}
                                </p>
                                <p className="mt-0.5 truncate text-xs text-slate-400" data-testid={`vst-last-${v.session_id}`}>
                                    {v.last_message?.text || tx.vstNoMessages}
                                </p>
                                <div className="mt-1.5 flex items-center gap-1.5">
                                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${v.active
                                        ? 'border-emerald-400/30 bg-emerald-500/15 text-emerald-300'
                                        : 'border-white/10 bg-slate-700/40 text-slate-400'}`}>
                                        {v.active ? tx.vstActive : tx.vstEnded}
                                    </span>
                                    {v.live && (
                                        <span className="rounded-full border border-cyan-400/30 bg-cyan-500/15 px-2 py-0.5 text-[10px] font-medium text-cyan-200"
                                            data-testid={`vst-live-${v.session_id}`}>{tx.vstAtDoorNow}</span>
                                    )}
                                    <span className="text-[10px] text-slate-500">{fmtTime(v.started_at, lang)}</span>
                                </div>
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                                {v.unread > 0 && (
                                    <span className="min-w-[1.25rem] rounded-full bg-rose-500 px-1.5 py-0.5 text-center text-[11px] font-bold text-white"
                                        data-testid={`vst-unread-${v.session_id}`}>{v.unread}</span>
                                )}
                                <ChevronRight className="h-5 w-5 text-slate-500" />
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default VisitorsScreen;
